import {
  StateNodeConfig,
  StateSchema,
  ConditionPredicate,
  createMachine,
  MachineOptions,
  assign as ogAssign,
  StateMachine,
  State,
  Interpreter,
  actions,
} from 'xstate';
import { assign } from '@xstate/immer';
import { ValidatorFn } from '../config/validators';
import { TransformerFn } from '../config/transformers';
import { current, isDraft } from 'immer';

const { pure } = actions;

export type Transformers =
  | string
  | TransformerDescriptor
  | (TransformerDescriptor | string)[];

export type TransformerDescriptor = {
  fn: string;
  args?: any[];
  target?: keyof FormField; // defaults to 'value'. Corresponds to the FormField attribute that has to be transformed.
};

export type TransformersMap = { [fnName: string]: TransformerFn };

export type Validators =
  | string
  | ValidatorDescriptor
  | (ValidatorDescriptor | string)[];

export interface ValidatorDescriptor {
  fn: string;
  args?: any[];
}

export interface Effect extends Omit<FormField, 'name' | 'dependsOn'> {}

export interface Condition {
  name: string;
  condition?: Validators;
  transformers?: Transformers;
}

export type SimplifiedCondition = Condition & Effect;
export type SimplifiedConditions = { conditions: Condition[] } & Effect;

export type Dependency = { conditions: Condition[]; effect: Effect };
export type DependsOnFn = (
  value: FormField,
  otherFields: FormField[]
) => FormField;
export interface ValidateAs<T = unknown> {
  (value: T, fields?: FormField[]): T;
}
export interface FormField {
  name: string;
  formName?: string;
  defaultValue?: any;
  transformers?: Transformers;
  validator?: Validators | ReturnType<ValidatorFn> | ReturnType<ValidatorFn>[];
  errors?: string[];
  value?: string | number;
  dependsOn?:
    | Dependency
    | SimplifiedCondition
    | SimplifiedConditions
    | Array<Dependency | SimplifiedCondition | SimplifiedConditions>
    | DependsOnFn;
  active?: boolean;
  type?: string; // an arbitrary string that represent the type of Field, in the comparator application it maps to a Component (example: BasicInput)
  label?: string;
  tooltip?: string;
  subLabel?: string;
  options?: { label: string; value: string | number }[];
  meta?: { [key: string]: any };
  validateOn?: 'blur' | 'change';
  validateAs?: ValidateAs<any>;
  validators?: any;
  propMap?: any;
}

export type FormMachine = StateMachine<FormContext, FormSchema, FormEvent>;
export type FormInterpreter = Interpreter<FormContext, FormSchema, FormEvent>;

export interface FormFieldWithValidator extends Omit<FormField, 'validator'> {
  validator?: ReturnType<ValidatorFn>;
}

export interface FormContext {
  name: string;
  label?: string;
  fields: Array<FormField>;
  externalFields?: Array<FormField>;
  validators: { [fnName: string]: ValidatorFn };
  transformers: TransformersMap;
  originalFields?: Array<FormField>;
  keepAlive?: boolean;
}

export interface FormSchema extends StateSchema<FormContext> {
  states: {
    incomplete: {};
    errors: {};
    completed: {};
    submitting: {};
    submitted: {};
    updated: {};
    onChangeValidation: {};
  };
}

export type FormState = State<FormContext, FormEvent, FormSchema>;

export type FormConfig = MachineOptions<FormContext, FormEvent> & {
  onDone: (state: FormSchema) => void;
};

export interface ValidationSuccessEvent {
  type: 'done.invoke.formValidation';
  data: { [key: string]: string };
}

export interface ValidationErrorEvent {
  type: 'error.platform.formValidation';
  data: string | Error;
}

export interface OnChangeEventSuccess {
  type: 'done.invoke.onChangeAsynchronousValidation';
  data: { [key: string]: string };
}

export interface OnChangeEventError {
  type: 'done.invoke.onChangeAsynchronousValidation';
  data: { [key: string]: string };
}
interface PartialChangeEvent
  extends Partial<Omit<React.ChangeEvent<HTMLInputElement>, 'target'>> {
  target: Partial<
    Omit<React.ChangeEvent<HTMLInputElement>['target'], 'value'>
  > &
    Pick<React.ChangeEvent<HTMLInputElement>['target'], 'name'> & {
      value: FormField['value'];
    };
}
export interface OldChangeEvent extends PartialChangeEvent {
  type: 'CHANGE';
}

export type ChangeEvent =
  | OldChangeEvent
  | (Omit<React.ChangeEvent<HTMLInputElement>, 'type'> & { type: 'change' });

export type ChangeEventHandler = (e: PartialChangeEvent) => void;

export interface BlurEvent extends React.FocusEvent<HTMLInputElement> {
  type: 'blur';
}

export type SetEvent =
  | {
      type: 'SET';
      fields: FormField[];
      externalFields?: FormField[];
      addField?: boolean;
      replaceFields?: boolean;
      checkErrors?: boolean;
    }
  | {
      type: 'SET';
      fields?: FormField[];
      externalFields: FormField[];
      addField?: boolean;
      replaceFields?: boolean;
      checkErrors?: boolean;
    };

export interface SubmitEvent {
  type: 'SUBMIT';
}

export type FormEvent =
  | BlurEvent
  | ChangeEvent
  | SetEvent
  | OnChangeEventError
  | OnChangeEventSuccess
  | ValidationErrorEvent
  | ValidationSuccessEvent
  | SubmitEvent
  | {
      type: 'xstate.init';
    };

const CHANGE = {
  actions: ['setValue', 'applyConditionals', 'applyTransformers'],
  target: 'onChangeValidation',
};

const blur = {
  actions: ['setErrors'],
  target: 'updated',
};

const SET = {
  actions: ['setFields', 'applyConditionals', 'setErrors', 'applyTransformers'],
  target: 'updated',
};

const SUBMIT = {
  target: 'submitting',
  actions: ['applyConditionals', 'setErrors'],
};

const baseTransitions = {
  CHANGE,
  change: CHANGE,
  blur,
  SUBMIT,
};

export const formStateNodes: StateNodeConfig<
  FormContext,
  FormSchema,
  FormEvent
> = {
  id: 'form',
  initial: 'updated',
  on: {
    SET,
  },
  states: {
    incomplete: {
      entry: 'emit',
      on: {
        ...baseTransitions,
      },
    },
    errors: {
      entry: 'emit',
      on: {
        ...baseTransitions,
      },
    },
    completed: {
      entry: 'emit',
      on: {
        ...baseTransitions,
      },
    },
    submitting: {
      invoke: {
        src: {
          id: 'formValidation',
          type: 'formValidation',
        },
        onDone: [
          {
            cond: (ctx) => !!ctx.keepAlive,
            target: 'completed',
            actions: 'onValidationSuccess',
          },
          {
            target: 'submitted',
            actions: 'onValidationSuccess',
          },
        ],
        onError: {
          target: 'errors',
          actions: 'onValidationError',
        },
      },
    },
    submitted: {
      entry: ['emit', 'killed'],
      type: 'final',
    },
    onChangeValidation: {
      on: {
        ...baseTransitions,
      },
      invoke: {
        src: {
          id: 'onChangeAsynchronousValidation',
          type: 'onChangeAsynchronousValidation',
        },
        onDone: {
          target: 'updated',
          actions: 'onChangeAsynchronousValidationSuccess',
        },
        onError: {
          target: 'updated',
          actions: 'onChangeAsynchronousValidationError',
        },
      },
    },
    updated: {
      entry: ['init'],
      always: [
        { target: 'completed', cond: 'hasNoValidators' },
        { target: 'incomplete', cond: 'hasNoValues' },
        {
          target: 'errors',
          cond: 'hasIncorrectValues',
        },
        { target: 'completed' },
      ],
    },
  },
};

type FormPredicate = ConditionPredicate<FormContext, FormEvent>;

export const checkFormValues = ({
  fields,
  validators,
}: Pick<FormContext, 'fields' | 'validators'>) => {
  // exclude fields that are not visible but have validation
  const fieldsToValidate = fields.filter((f) => {
    if (f['visibleExpression'] === undefined) return true;
    return f['visibleExpression'].value === 'true';
  });

  for (const field of fieldsToValidate) {
    const errors = validate(field, { fields, validators });
    if (errors && errors.length > 0) return true;
  }
  return false;
};

const guards: { [name: string]: FormPredicate } = {
  hasNoValidators: ({ fields }) => {
    for (const field of fields) {
      if (field.validator !== undefined && field.active !== false) {
        return false;
      }
    }
    return true;
  },
  hasNoValues: ({ fields }) => {
    for (const field of fields) {
      if (field.value !== undefined) {
        return false;
      }
    }
    return true;
  },
  hasIncorrectValues: ({ fields, validators }) => {
    return checkFormValues({ fields, validators });
  },
};

const formValidation =
  ({ fields, validators }: FormContext) =>
  async () => {
    if (checkFormValues({ fields, validators }))
      throw new Error('The form contains errors');
  };
const onChangeAsynchronousValidation = async () => true;
const onChangeAsynchronousValidationSuccess = assign<FormContext, FormEvent>(
  (_) => {
    /* not implemented by default */
  }
);
const onChangeAsynchronousValidationError = assign<FormContext, FormEvent>(
  (_) => {
    /* not implemented by default */
  }
);

//NB: should create a utils function that can take T | T[] and return T[]

/**
 * @description parses all format of transformers and return an actual function
 * @param transformer transformer descriptor, transformer fn name or Function
 * @param transformers list of available transformers
 * @returns actual transformer function
 */
export const parseTransformer = (
  transformer: Transformers | undefined,
  transformers: TransformersMap | undefined
) => {
  if (!transformer || !transformers) return [];
  let parsedTransformer: (TransformerDescriptor | string)[];
  const transformerFns: {
    transformerFn: ReturnType<TransformerFn>;
    targetAttribute: string;
  }[] = [];
  if (!Array.isArray(transformer)) parsedTransformer = [transformer];
  else parsedTransformer = transformer;
  for (const transformerDescriptor of parsedTransformer) {
    let fnName;
    let args;
    let targetAttribute;
    if (typeof transformerDescriptor === 'string') {
      fnName = transformerDescriptor;
      args = [];
      targetAttribute = 'value';
    }
    if (typeof transformerDescriptor === 'object') {
      fnName = transformerDescriptor.fn;
      args = transformerDescriptor.args;
      targetAttribute = transformerDescriptor.target ?? 'value';
    }
    if (transformers[fnName]) {
      transformerFns.push({
        transformerFn: transformers[fnName](...args),
        targetAttribute,
      });
    }
  }
  return transformerFns;
};

export const getValidatorFns = (
  descriptor: FormField['validator'],
  validators: FormContext['validators']
): Array<ReturnType<ValidatorFn>> | undefined => {
  if (!descriptor) return undefined;
  let fieldValidators: Array<
    string | ValidatorDescriptor | ReturnType<ValidatorFn>
  >;
  if (!Array.isArray(descriptor)) fieldValidators = [descriptor];
  else fieldValidators = descriptor;
  const returnedValidators: ReturnType<ValidatorFn>[] = [];
  for (const validatorDescriptor of fieldValidators) {
    if (typeof validatorDescriptor === 'function') {
      returnedValidators.push(validatorDescriptor);
      continue;
    }
    let fnName: string, fnArgs: ValidatorDescriptor['args'];
    if (typeof validatorDescriptor === 'string') {
      fnName = validatorDescriptor;
      fnArgs = [];
    } else {
      fnName = validatorDescriptor.fn;
      fnArgs = validatorDescriptor.args ?? [];
    }
    if (validators[fnName])
      returnedValidators.push(validators[fnName](...fnArgs));
  }
  return returnedValidators;
};

const init = pure<FormContext, FormEvent>((context) => {
  // ie. if it is the actual instantiation
  if (context.originalFields) return;
  const assignOriginalFields = ogAssign<FormContext, FormEvent>((ctx) => {
    return { ...ctx, originalFields: ctx.fields };
  });
  return [assignOriginalFields, applyConditionals, applyTransformers];
});

const getFieldsImpactedByConditional = (
  { fields }: FormContext,
  event: FormEvent
) => {
  return event.type.toUpperCase() === 'CHANGE'
    ? fields.filter((f) => {
        if (f.name === (event as ChangeEvent).target.name) return true;
        if (!f.dependsOn) return false;
        if (typeof f.dependsOn === 'function') return true;
        const dependencies = convertSimplifiedCondition(f.dependsOn);
        return dependencies.find((dep) =>
          dep.conditions.find(
            (condition) => condition.name === (event as ChangeEvent).target.name
          )
        );
      })
    : fields;
};

const applyConditionals = ogAssign<FormContext, FormEvent>((context, event) => {
  if (
    event.type.toUpperCase() !== 'CHANGE' &&
    event.type !== 'SET' &&
    event.type !== 'xstate.init'
  )
    return context;
  // TODO: check if that filter is really useful
  // if so, at least filter the impacted fields on SET based on event.fields
  const impactedFields = getFieldsImpactedByConditional(context, event);
  const updatedContext = { ...context, fields: [...context.fields] };
  for (const field of impactedFields) {
    const i = context.fields.findIndex((f) => field.name === f.name);
    if (i > -1) {
      updatedContext.fields[i] =
        typeof field.dependsOn === 'function'
          ? field.dependsOn(field, updatedContext.fields)
          : getFieldWithDependencies(field, event, context);
    }
  }
  return updatedContext;
});

export const validate = (
  field: Pick<FormField, 'value' | 'validator' | 'active' | 'validateAs'> & {
    name?: string;
  },
  {
    fields = [],
    validators: ctxValidators,
  }: Pick<FormContext, 'validators'> & { fields?: FormContext['fields'] }
): string[] | undefined => {
  if (!field.active) return undefined;
  const validators = getValidatorFns(field.validator, ctxValidators) ?? [];
  let errors: string[] = [];
  for (const validator of validators) {
    const value = field.validateAs
      ? field.validateAs(field.value, fields)
      : field.value;
    try {
      errors = [...errors, ...(validator(value, fields) ?? [])];
    } catch (error) {
      console.warn(field.name ?? '', 'validator execution error:', error);
    }
  }
  return errors.length > 0 ? errors : undefined;
};

export const convertSimplifiedCondition = (
  condition: FormField['dependsOn']
): Dependency[] => {
  let conditionAsArray = Array.isArray(condition) ? condition : [condition];
  let ogDependsOn = conditionAsArray;
  let dependsOn: Dependency[] = [...conditionAsArray] as Dependency[];
  for (let i = 0; i < ogDependsOn.length; i++) {
    if (!(ogDependsOn[i] as Dependency).conditions) {
      const {
        name,
        condition: _condition,
        transformers,
        ...effect
      } = ogDependsOn[i] as SimplifiedCondition;
      dependsOn[i] = {
        conditions: [
          {
            name: (ogDependsOn[i] as SimplifiedCondition).name,
            condition: (ogDependsOn[i] as SimplifiedCondition).condition,
            transformers: (ogDependsOn[i] as SimplifiedCondition).transformers,
          },
        ],
        effect,
      } as Dependency;
    } else if (!(ogDependsOn[i] as Dependency).effect) {
      const { conditions, ...effect } = ogDependsOn[i] as SimplifiedConditions;
      dependsOn[i] = {
        conditions,
        effect,
      } as Dependency;
    }
  }
  return dependsOn;
};

export const evaluateCondition = (
  condition: Condition,
  context: FormContext
): boolean => {
  const { validators } = context;
  const relatedField = getRelatedField(condition.name, context);
  if (!relatedField) {
    // console.warn(
    //   'Conditionals: the following field does not exist: ' +
    //     condition.name +
    //     '\n This condition will always be false.'
    // );
    return false;
  }
  const conditionValidators = getValidatorFns(condition.condition, validators);
  // by default conditions without validators are considered false. A "always true validator" has to be explicitly set
  if (
    !conditionValidators ||
    (Array.isArray(conditionValidators) && conditionValidators.length < 1)
  ) {
    // console.warn(
    //   'Conditionals: could not find the corresponding validator:',
    //   condition.condition,
    //   '\n This condition will always be false.'
    // );
    return false;
  }
  for (const validator of conditionValidators) {
    const currentConditionIsFulfilled =
      validator(relatedField.value) === undefined;
    // if one condition of the current dependency is false it's not necessary to evaluate the others
    if (!currentConditionIsFulfilled) return false;
  }
  return true;
};

const getRelatedField = (
  name: string,
  { fields, externalFields }: FormContext
): FormField | undefined => {
  return (
    fields.find((f) => name === f.name) ??
    (externalFields ?? []).find((f) => name === f.name)
  );
};

export const applyEffectsToField = (
  field: FormField,
  dependency: Dependency,
  context: FormContext
): FormField => {
  let updatedField = { ...field };
  const { transformers } = context;
  for (const formAttribute in dependency.effect) {
    // apply all the effects of the dependency
    updatedField[formAttribute] = dependency.effect[formAttribute];
  }
  for (const condition of dependency.conditions) {
    // apply the transformers of the dependency
    const relatedField = getRelatedField(condition.name, context);
    if (relatedField && condition.transformers) {
      updatedField = transformField(
        updatedField,
        transformers,
        condition.transformers,
        relatedField
      );
    }
  }
  return updatedField;
};

export const applyConditionalsToField = (
  field: FormField,
  dependenciesArray: Dependency[],
  context: FormContext,
  currentEvent: FormEvent
): FormField => {
  let updatedField = { ...field };
  let isConditionFulfilled: boolean | undefined = false;
  for (const dependency of dependenciesArray) {
    for (const condition of dependency.conditions) {
      isConditionFulfilled = evaluateCondition(condition, context);
      // if (isConditionFulfilled === undefined) continue;
      if (!isConditionFulfilled) break;
    }
    if (isConditionFulfilled) {
      updatedField = applyEffectsToField(updatedField, dependency, context);
      // if the current field is the one that triggered the onChange event we want to use the value from the FormEvent
      const currentFieldWasChanged =
        currentEvent.hasOwnProperty('target') &&
        (currentEvent as ChangeEvent).target.name === updatedField.name;
      if (currentFieldWasChanged) {
        // if the value or the validator of the field changed after applying effects the transformers have to be applied
        updatedField.value = (currentEvent as ChangeEvent).target.value;
        updatedField = transformField(updatedField, context.transformers);
      }
      // break the loop if condition is fulfilled, because we want to apply the first one that is true
      break;
    } else {
      // in case the condition was fulfilled before the latest ChangeEvent and if it's not anymore fulfilled we want to reset it to the
      const ogField = (context.originalFields ?? []).find(
        (f) => f.name === field.name
      );
      if (ogField) {
        for (const formAttribute in ogField) {
          if (formAttribute === 'errors') continue;
          if (formAttribute !== 'value')
            updatedField[formAttribute] = ogField[formAttribute];
          else
            updatedField[formAttribute] =
              updatedField[formAttribute] ?? ogField[formAttribute];
        }
      }
    }
  }
  return updatedField;
};

export const getFieldWithDependencies = (
  field: FormField,
  currentEvent: FormEvent,
  context: FormContext
): FormField => {
  let updatedField = field;
  if (!context.fields || !field.dependsOn) return updatedField;
  const dependsOnArray = convertSimplifiedCondition(field.dependsOn);
  // loop over all the different possible conditions: first one that is true is applied, the rest is ignored (ie. order of conditions matters)
  return applyConditionalsToField(
    updatedField,
    dependsOnArray,
    context,
    currentEvent
  );
};

/**
 *
 * @param transformers transformer descriptor, transformer fn name or actual Function
 * @param transformersMap list of transformer definitions: { fnName: () => { ...implementation } }
 * @param field a FormField object to which we want to apply the transformers to
 * @param relatedField another FormField on which the field param might be dependant on
 * @returns field with transformed attributes
 */
export const transformField = (
  field: FormField,
  transformersMap: TransformersMap,
  transformers?: Transformers | undefined,
  relatedField?: FormField
): FormField => {
  const updatedField = field;
  const transformersFn = parseTransformer(
    transformers ?? field.transformers,
    transformersMap
  );
  for (const { transformerFn, targetAttribute } of transformersFn) {
    updatedField[targetAttribute] = transformerFn(
      updatedField.value,
      relatedField ? relatedField.value : undefined
    );
  }
  return updatedField;
};

const getTransformedFields = ({ name, newValue, fields, transformers }) => {
  const fieldIndex = fields.findIndex((f) => f.name === name);
  if (fieldIndex > -1) {
    const field = { ...fields[fieldIndex], value: newValue };
    const updatedField = transformField(field, transformers);
    const updatedFields = [...fields];
    updatedFields.splice(fieldIndex, 1, updatedField);
    return updatedFields;
  }
  return fields;
};

const applyTransformers = ogAssign<FormContext, FormEvent>((ctx, e) => {
  if (e.type.toUpperCase() === 'CHANGE') {
    const value = (e as ChangeEvent).target.value;
    const name = (e as ChangeEvent).target.name;
    const updatedFields = getTransformedFields({
      name,
      newValue: value,
      fields: ctx.fields,
      transformers: ctx.transformers,
    });
    return { ...ctx, fields: updatedFields };
  } else if (e.type === 'SET') {
    let updatedFields = [...ctx.fields];
    for (const field of e.fields ?? []) {
      updatedFields = getTransformedFields({
        name: field.name,
        newValue: field.value,
        fields: updatedFields,
        transformers: ctx.transformers,
      });
    }
    return { ...ctx, fields: updatedFields };
  } else if (e.type === 'xstate.init') {
    let updatedFields = [...ctx.fields];
    for (const field of ctx.fields) {
      updatedFields = getTransformedFields({
        name: field.name,
        newValue: field.value,
        fields: updatedFields,
        transformers: ctx.transformers,
      });
    }
    return { ...ctx, fields: updatedFields };
  }
  return ctx;
});

const setValue = assign<FormContext, FormEvent>(({ fields }, event) => {
  if (event.type.toUpperCase() !== 'CHANGE') return;
  const i = fields.findIndex((f) => {
    return f.name === (event as ChangeEvent).target.name;
  });
  if (i < 0) return;
  fields[i].value = (event as ChangeEvent).target.value;
});

const setErrorsOnBlur = ({ fields, validators }, event) => {
  const i = fields.findIndex((f) => {
    return f.name === event.target.name;
  });
  if (i < 0) return;
  fields[i].errors = validate(fields[i], { fields, validators });
};

const setErrors = assign<FormContext, FormEvent>(
  ({ fields, validators }, event) => {
    if (event.type === 'SET' && event.checkErrors === false) return;
    if (event.type === 'blur') {
      setErrorsOnBlur({ fields, validators }, event);
    } else if (event.type === 'SET') {
      for (const field of event.fields ?? []) {
        const i = fields.findIndex((f) => {
          return f.name === field.name;
        });
        if (i < 0) continue;
        fields[i].errors = field.hasOwnProperty('errors')
          ? field.errors
          : validate(field, { fields, validators });
      }
    } else {
      let i = 0;
      for (const field of fields) {
        fields[i].errors = validate(field, { fields, validators });
        i++;
      }
    }
  }
);

const setFields = assign<FormContext, FormEvent>(
  ({ fields, externalFields = [] }, event) => {
    if (event.type !== 'SET') return;
    for (const field of event.fields ?? []) {
      let i = fields.findIndex((f) => {
        return f.name === field.name;
      });

      if (i < 0 && event.addField === true) {
        i = fields.length;
        fields[i] = { ...field };
      }

      if (i < 0) continue;

      fields[i] = {
        ...(isDraft(fields[i]) ? current(fields[i]) : {}),
        ...field,
      };
    }

    if (event.replaceFields) {
      fields.splice(0, fields.length, ...(event.fields ?? []));
    }

    setExternalFields(event, externalFields);
  }
);

const setExternalFields = (event, externalFields) => {
  for (const field of event.externalFields ?? []) {
    const i = externalFields.findIndex((f) => {
      return f.name === field.name;
    });
    if (i < 0) continue;
    externalFields[i] = {
      ...(isDraft(externalFields[i]) ? current(externalFields[i]) : {}),
      ...field,
    };
  }
};

/* I will re-implement those features later when there is more time (it is just for optimization)

const isStringArrayOrUndefined = (result: string[] | undefined) => {
  return (
    (Array.isArray(result) && result.every((s) => typeof s === 'string')) ||
    typeof result === 'undefined'
  );
};

const testValidator = (
  fn: ReturnType<ValidatorFn>,
  fnName: string | undefined
) => {
  try {
    const testString = isStringArrayOrUndefined(fn(''));
    const testNumber = isStringArrayOrUndefined(fn(1));
    const testUndefined = isStringArrayOrUndefined(fn(undefined));
    return testString && testNumber && testUndefined;
  } catch (e) {
    console.warn(
      'the validator function "' + fnName + '" provoked the following error:',
      e
    );
    return false;
  }
};
*/
export const options: Partial<MachineOptions<FormContext, FormEvent>> = {
  guards,
  actions: {
    init,
    setValue,
    setErrors,
    setFields,
    onChangeAsynchronousValidationSuccess,
    onChangeAsynchronousValidationError,
    applyConditionals,
    applyTransformers,
    emit: () => undefined,
    killed: () => undefined,
    onValidationError: ({ fields }) => {
      console.warn(
        'the following fields contain errors',
        fields.filter((f) => f.errors)
      );

      const firstFieldWithError = fields.find(
        (field) => field.errors && field.errors.length > 0
      );

      if (firstFieldWithError) {
        setTimeout(() => {
          const fieldElement = document.querySelector(
            `[name="${firstFieldWithError.name}"]`
          ) as HTMLElement;

          if (fieldElement && 'focus' in fieldElement) {
            fieldElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });

            setTimeout(() => {
              fieldElement.focus();
            }, 200);
          }
        }, 0);
      }
    },
    onValidationSuccess: () => {
      return;
    },
  },
  services: { formValidation, onChangeAsynchronousValidation },
};

export const formMachine = createMachine<FormContext, FormEvent>(
  formStateNodes,
  options
);
